import { type FormEvent, useEffect, useState } from "react";

import AppShell, { EmptyState, ErrorNote } from "@/components/AppShell";
import SchemaForm, { type FieldDescriptor } from "@/components/SchemaForm";
import { api } from "@/lib/api";
import { useAsync } from "@/lib/hooks";
import type { Equipment as EquipmentItem } from "@/types";

const CATEGORIES = [
  "pump",
  "filter",
  "feeder",
  "heater",
  "cleaner",
  "liner",
  "cover",
  "sanitizer_system",
  "other",
] as const;

type Category = (typeof CATEGORIES)[number];

interface CategorySchema {
  category: Category;
  label: string;
  fields: FieldDescriptor[];
}

export default function Equipment() {
  const { data, error, loading, reload } = useAsync<EquipmentItem[]>(
    () => api.get<EquipmentItem[]>("/api/equipment"),
    [],
  );
  const [adding, setAdding] = useState(false);
  const [replacing, setReplacing] = useState<EquipmentItem | null>(null);

  const active = (data ?? []).filter((item) => item.is_active);
  const retired = (data ?? []).filter((item) => !item.is_active);

  return (
    <AppShell
      title="Equipment"
      subtitle={data ? `${active.length} installed` : undefined}
      action={
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="btn btn-primary px-3 py-2 text-sm"
        >
          Add
        </button>
      }
    >
      {error && <ErrorNote message={error} />}
      {loading && <p className="text-sm text-(--color-ink-muted)">Loading…</p>}

      {data && data.length === 0 && (
        <EmptyState
          title="No equipment recorded"
          body="Recording the pump matters most — the pump-schedule form changes shape depending on whether it's variable-speed."
          action={
            <button type="button" onClick={() => setAdding(true)} className="btn btn-primary">
              Add equipment
            </button>
          }
        />
      )}

      {active.length > 0 && (
        <ul className="space-y-2">
          {active.map((item) => (
            <li key={item.id}>
              <ItemCard item={item} onReplace={() => setReplacing(item)} />
            </li>
          ))}
        </ul>
      )}

      {retired.length > 0 && (
        <section className="mt-6">
          <h2 className="unit mb-2">Previously installed</h2>
          <ul className="space-y-2">
            {retired.map((item) => (
              <li key={item.id}>
                <ItemCard item={item} />
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-(--color-ink-faint)">
            Retired equipment is kept so past readings still show what was installed at the time.
          </p>
        </section>
      )}

      {adding && (
        <EquipmentDialog
          title="Add equipment"
          onClose={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            reload();
          }}
        />
      )}

      {replacing && (
        <EquipmentDialog
          title={`Replace ${replacing.category.replace(/_/g, " ")}`}
          replacingId={replacing.id}
          fixedCategory={replacing.category as Category}
          onClose={() => setReplacing(null)}
          onSaved={() => {
            setReplacing(null);
            reload();
          }}
        />
      )}
    </AppShell>
  );
}

function ItemCard({ item, onReplace }: { item: EquipmentItem; onReplace?: () => void }) {
  const spec = Object.entries(item.spec ?? {}).filter(
    ([key, value]) => key !== "category" && value !== null && value !== "",
  );

  return (
    <div className="card p-4">
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <p className="unit">{item.category.replace(/_/g, " ")}</p>
          <p className="mt-0.5 font-medium">
            {[item.manufacturer, item.model].filter(Boolean).join(" ") || "Unspecified"}
          </p>
        </div>
        {onReplace && (
          <button type="button" onClick={onReplace} className="btn btn-quiet shrink-0 px-3 py-2 text-sm">
            Replace
          </button>
        )}
      </div>

      {spec.length > 0 && (
        <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-(--color-ink-muted)">
          {spec.map(([key, value]) => (
            <div key={key}>
              <dt className="inline">{key.replace(/_/g, " ")}: </dt>
              <dd className="inline">{String(value)}</dd>
            </div>
          ))}
        </dl>
      )}

      <p className="mt-2 text-xs text-(--color-ink-faint)">
        {item.is_active
          ? item.installed_on
            ? `Installed ${item.installed_on}`
            : "Installed"
          : `Removed ${item.removed_on}${item.removal_reason ? ` — ${item.removal_reason}` : ""}`}
      </p>
    </div>
  );
}

function EquipmentDialog({
  title,
  replacingId,
  fixedCategory,
  onClose,
  onSaved,
}: {
  title: string;
  replacingId?: string;
  fixedCategory?: Category;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [category, setCategory] = useState<Category>(fixedCategory ?? "pump");
  const [schema, setSchema] = useState<CategorySchema | null>(null);
  const [spec, setSpec] = useState<Record<string, unknown>>({});
  const [manufacturer, setManufacturer] = useState("");
  const [model, setModel] = useState("");
  const [serial, setSerial] = useState("");
  const [installedOn, setInstalledOn] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSpec({});
    void api
      .get<CategorySchema>(`/api/equipment/schema/${category}`)
      .then(setSchema)
      .catch(() => setSchema(null));
  }, [category]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    // Blank fields are omitted rather than sent as empty strings — the API
    // validates the spec and would reject "" where it wants a number.
    const cleanedSpec: Record<string, unknown> = { category };
    for (const [key, value] of Object.entries(spec)) {
      if (value !== null && value !== "") cleanedSpec[key] = value;
    }

    const payload = {
      category,
      manufacturer: manufacturer || null,
      model: model || null,
      serial: serial || null,
      installed_on: installedOn || null,
      spec: cleanedSpec,
    };

    try {
      if (replacingId) {
        await api.post(`/api/equipment/${replacingId}/replace`, {
          removed_on: installedOn,
          removal_reason: reason || null,
          replacement: payload,
        });
      } else {
        await api.post("/api/equipment", payload);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save that.");
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center sm:p-6"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <form
        onSubmit={submit}
        className="max-h-[88dvh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-(--color-surface-raised) p-5 sm:rounded-2xl"
      >
        <div className="flex items-baseline justify-between">
          <h2 className="text-lg font-semibold capitalize">{title}</h2>
          <button type="button" onClick={onClose} className="text-sm text-(--color-ink-muted)">
            Close
          </button>
        </div>

        <div className="mt-4 space-y-3">
          {!fixedCategory && (
            <div>
              <label htmlFor="category" className="text-sm font-medium">
                Type
              </label>
              <select
                id="category"
                value={category}
                onChange={(e) => setCategory(e.target.value as Category)}
                className="field mt-1.5 capitalize"
              >
                {CATEGORIES.map((option) => (
                  <option key={option} value={option}>
                    {option.replace(/_/g, " ")}
                  </option>
                ))}
              </select>
            </div>
          )}

          <input
            value={manufacturer}
            onChange={(e) => setManufacturer(e.target.value)}
            placeholder="Manufacturer — e.g. Pentair"
            className="field"
          />
          <input
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder="Model — e.g. Challenger"
            className="field"
          />
          <input
            value={serial}
            onChange={(e) => setSerial(e.target.value)}
            placeholder="Serial number (optional)"
            className="field"
          />
          <div>
            <label htmlFor="installed" className="text-sm font-medium">
              {replacingId ? "Swap date" : "Installed on"}
            </label>
            <input
              id="installed"
              type="date"
              value={installedOn}
              onChange={(e) => setInstalledOn(e.target.value)}
              className="field mt-1.5"
            />
          </div>

          {replacingId && (
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why was the old one removed?"
              className="field"
            />
          )}
        </div>

        {schema && schema.fields.length > 0 && (
          <div className="mt-5">
            <h3 className="unit mb-2">{schema.label} details</h3>
            <SchemaForm
              fields={schema.fields}
              values={spec}
              onChange={(name, value) => setSpec((prev) => ({ ...prev, [name]: value }))}
            />
          </div>
        )}

        {error && (
          <div className="mt-4">
            <ErrorNote message={error} />
          </div>
        )}

        <button type="submit" disabled={busy} className="btn btn-primary mt-5 w-full">
          {busy ? "Saving…" : replacingId ? "Replace it" : "Add it"}
        </button>

        {replacingId && (
          <p className="mt-2 text-xs text-(--color-ink-faint)">
            The old one is retired, not deleted, and the swap is logged as an event.
          </p>
        )}
      </form>
    </div>
  );
}
